import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowLeft from '../../Assets/Arrow left.svg';
import CouponName from './Components/CouponName';
import AddCouponImage from './Components/AddCouponImage';
import SelectCollectionOrProduct from './Components/SelectCollectionOrProduct';
import PurchaseRequirement from './Components/PurchaseRequirement';
import CouponLimitation from './Components/CouponLimitation';
import AddDuration from './Components/AddDuration';
import CropImage from '../../Components/CropImage/CropImage';
import Modal from '../CouponManagement/Components/Modals/Modal';
import { CropImageContext } from '../../Context/CropImageContext';
import { AddCouponFormContext } from '../../Context/Coupons/AddCouponFormContext';
import SetFixedPrice from './Components/SetFixedPrice';
import imageCompression from 'browser-image-compression';
import { formatDateToMMDDYYYY, getMediaUrl, isDateExpired } from '../../lib/utils/utils';
import { showToast } from '../../Components/Toaster/Toaster';
import { updateCoupon, getCategory, getCollection, getCustomCollection } from '../../lib/utils/apiCalls';
import { v4 as uuidv4 } from 'uuid';
import { apiV1 } from '../../Auth/Interceptor';

function EditFixedDiscount() {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { modalOpen } = useContext(CropImageContext);

    const [coupon, setCoupon] = useState({});

    const {
        formInputs,
        image,
        setFormErrors,
        defaultFormErrors,
        date,
        setDate,
        purchaseRequirement,
        limitType,
        selectedProducts,
        selectedCollection,
        selectedCustomCollection,
        setFormInputs,
        setImage,
        setPurchaseRequirement,
        setSelectionType,
        setSelectedProducts,
        setLimitType,
        setSelectedCollection,
        setSelectedCategory,
        setSelectedCustomCollection
    } = useContext(AddCouponFormContext);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        if(modalOpen) {
            setModalVisible(true);
        } else {
            setModalVisible(false);
        }
    }, [modalOpen]);

    const handleUpdateCoupon = async () => {
        const validationErrors = {};

        if(!formInputs.fixedPrice.trim()) {
            validationErrors.fixedPrice = 'Required field';
        }

        if(Object.keys(image).length === 0) {
            validationErrors.image = 'Image is required';
        }

        if((Object.keys(selectedCollection).length === 0 && Object.keys(selectedCustomCollection).length === 0 && selectedProducts.length === 0)) {
            validationErrors.collectionOrProduct = 'Collection or product is required';
        }

        if (!purchaseRequirement) {
            validationErrors.purchaseAmount = 'Select an option';
        }
        else if (purchaseRequirement === 'purchase-required') {
            if (!formInputs.purchaseAmount) {
                validationErrors.purchaseAmount = 'Purchase amount is required';
            }
            else if (formInputs.fixedPrice && (Number(formInputs.purchaseAmount) < Number(formInputs.fixedPrice))) {
                validationErrors.fixedPrice = 'Discount can\'t be greater than purchase amount.';
            }
        }
        
        if (!limitType) {
            validationErrors.limitAmount = "Select an option";
        } else if (limitType !== "no-limit") {
            if (!formInputs.limitAmount) {
                validationErrors.limitAmount = "Limit amount is required";
            }
        }
        
        if (!date.from && !date.to) {
            validationErrors.date = 'Duration is required';
        } else if (date.from && isDateExpired(date.from)) {
            validationErrors.date = 'Coupon duration expired. Please select new dates.';
        }

        if(Object.keys(validationErrors).length === 0) {
            setFormErrors(defaultFormErrors);

            try {
                let url;

                if(image.file) {
                    if(image.file.size > 1048576) {
                        try {
                            let compressedBlob = await imageCompression(image.file, {
                                maxSizeMB: 1,
                                maxWidthOrHeight: 1920,
                                useWebWorker: true
                            });
        
                            let compressedFile = new File([compressedBlob], image.file.name, { type: image.file.type, lastModified: Date.now() });
                        
                            if (compressedFile) {
                                url = await getMediaUrl(compressedFile);
                            }
                            else {
                                url = await getMediaUrl(image.file);
                            }
                        }
                        catch(err) {
                            url = await getMediaUrl(image.file);
                        }
                    }
                    else {
                        url = await getMediaUrl(image.file);
                    }
                }
                else {
                    url = image.url;
                }

                const callData = {
                    couponName: formInputs.name,
                    pic: url,
                    typeCoupon: "FIXED_DISCOUNTS",
                    limitUserBoolean: limitType === 'limit-order-per-customer' ? true : false,
                    limitNumber: Number(formInputs.limitAmount),
                    discountMoney: formInputs.fixedPrice,
                    startDate: formatDateToMMDDYYYY(date.from),
                    visibleDate: formatDateToMMDDYYYY(date.from),
                    endDate: formatDateToMMDDYYYY(date.to),
                    isExpired: false,
                    isVisible: true,
                }

                if(purchaseRequirement === 'purchase-required') {
                    callData.purchaseRequired = true;
                    callData.purchaseValue = formInputs.purchaseAmount;
                }
                else {
                    callData.purchaseRequired = false;
                }

                if(Object.keys(selectedCollection).length > 0) {
                    callData.selectTypeCoupon = 'CATEGORY';
                    callData.selectCollectionId = selectedCollection.id;
                }
                else if(Object.keys(selectedCustomCollection).length > 0) {
                    callData.selectTypeCoupon = 'CUSTOM_CATEGORY';
                    callData.customCollectionId = selectedCustomCollection.id;
                }
                else if(selectedProducts.length > 0) {
                    callData.selectTypeCoupon = 'PRODUCT';
                    callData.productList = selectedProducts.map(product => product.product.id);
                }

                const response = await updateCoupon(callData, params.couponId);
                if(response?.status === 200) {
                    showToast('Coupon updated.', 'success');
                    navigate(`${location.pathname.split('/').slice(0, -2).join('/')}`);
                }
                else {
                    showToast('Error updating coupon.', 'error');
                }
            }
            catch(err) {
                const errorMessage = err?.response?.data?.message || 'Error updating coupon.';
                showToast(errorMessage, "error");
            }
        }
        else {
            setFormErrors(validationErrors);
            return;
        }
    }

    useEffect(() => {
        const fetchCouponDetails = async (id) => {
            try {
                const response = await apiV1.get(`coupon/seller/${id}`);
                if(response?.status === 200) {
                    setCoupon(response.data.data);
                }
            }
            catch(err) {
                console.log(err);
            }
        }

        params.couponId && fetchCouponDetails(params.couponId);
    }, [params]);

    useEffect(() => {
        const fetchCollectionData = async (id) => {
            try {
                const response = await getCollection(id);
                if(response?.status === 200) {
                    return response.data.data.collection;
                }
            }
            catch(err) {
                console.log(err);
            }
            return null;
        }

        const fetchProductData = async (id) => {
            try {
                const response = await apiV1.get(`inventory/${id}`);
                if (response?.status === 200) {
                    return response.data.data;
                }
                else {
                    return null;
                }
            }
            catch (err) {
                console.log(err);
            }
            return null;
        };

        const fetchCategoryData = async (id) => {
            try {
                const response = await getCategory(id);
                if(response?.status === 200) {
                    return response.data.data.category;
                }
            }
            catch(err) {
                console.log(err);
            }
            return null;
        }

        const fetchCustomCollection = async (id) => {
            try {
                const response = await getCustomCollection(id);
                if(response?.status === 200) {
                    return response.data.data.collection;
                }
            }
            catch(err) {
                console.log(err);
            }
            return null;
        }

        const initializeForm = async () => {
            if (Object.keys(coupon).length === 0) return;

            setFormInputs((prev) => ({
                ...prev,
                name: coupon.couponName,
                fixedPrice: String(coupon.discountMoney),
                limitAmount: coupon.limitNumber ?? 0,
                ...(coupon.purchaseRequired && { purchaseAmount: coupon.purchaseValue })
            }));

            setPurchaseRequirement(coupon.purchaseRequired ? 'purchase-required' : 'purchase-not-required');
            setImage({ id: uuidv4(), url: coupon.pic, file: null });
            setDate({ from: coupon.startDate, to: coupon.endDate });
            const hasLimitNumber = coupon.limitNumber !== null;
            const isUserLimited = Boolean(coupon.limitUserBoolean);
            let type;

            if (!hasLimitNumber && !isUserLimited) {
                type = 'no-limit';
            } else if (isUserLimited) {
                type = 'limit-order-per-customer';
            } else {
                type = 'limit-number-purchase';
            }

            setLimitType(type);

            if(coupon.selectTypeCoupon === 'CATEGORY') {
                setSelectionType('category-collection');
                const collectionData = await fetchCollectionData(coupon.selectCollectionId);
                setSelectedCollection(collectionData);
                const categoryData = await fetchCategoryData(collectionData.CategoryId);
                setSelectedCategory(categoryData);
            }
            else if(coupon.selectTypeCoupon === 'CUSTOM_CATEGORY') {
                setSelectionType('custom-collection');
                const customCollection = await fetchCustomCollection(coupon.customCollectionId);
                setSelectedCustomCollection(customCollection);
            }
            else if(coupon.selectTypeCoupon === 'PRODUCT') {
                const list = await Promise.all(
                    coupon.productList.map(async (id) => {
                        const productData = await fetchProductData(id);
                
                        if (productData) {
                            return productData;
                        }
                        else {
                            return null;
                        }
                    })
                );

                const filteredList = list.filter(product => product !== null && product !== undefined);
                
                setSelectedProducts(filteredList);

                if(filteredList.length > 0) {
                    setSelectionType('product');
                }
            }
        }

        initializeForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coupon]);

    return (
        <div className="size-full overflow-y-scroll pb-16 outline-none">
            <div className="select-none flex items-center gap-2 relative m-[20px_19px_24px_20px]">
                <Link to={`${location.pathname.split('/').slice(0, -2).join('/')}`} className="p-[10px] flex items-center justify-center">
                    <img className='size-6 text-[#666666]' src={ArrowLeft} alt="" />
                </Link>

                <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">Fixed Discount Coupon &#40;{coupon?.couponName}&#41;</h1>
            </div>

            <div className="flex flex-col gap-6 mt-4 mx-5 w-[70%]">
                <div className="flex flex-col gap-4">
                    <CouponName />

                    <AddCouponImage />

                    <SelectCollectionOrProduct />

                    <SetFixedPrice />

                    <PurchaseRequirement />

                    <CouponLimitation />

                    <AddDuration />
                </div>

                <button onClick={handleUpdateCoupon} className='w-fit ml-auto px-4 py-2 rounded bg-primaryColor hover:bg-primaryAccent font-semibold text-base text-buttonText'>Update</button>
            </div>

            {
                modalOpen && 
                <div className={`${modalVisible ? 'opacity-100 visible bg-[#00000080]' : 'opacity-0 invisible'} transition-all duration-100 ease-in-out fixed z-50 w-full h-full top-0 left-0 flex justify-center items-center`}>
                    <CropImage />
                </div>
            }

            <Modal />
        </div>
    );
}

export default EditFixedDiscount;
